<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {stripeApi} from "@/container";
import {computed} from "vue";
import StripeSubscriptionService from "@/stripe/services/StripeSubscriptionService";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import {Skeleton} from "@/core/components/ui/skeleton";

const props = defineProps<{
    subscriptionId: number;
}>();

const {data, isLoading} = useSwr(
    `stripe/subscriptions/${props.subscriptionId}`,
    () => stripeApi().readSubscription(props.subscriptionId)
);

const recurringLabel = computed(() => {
    if (!data.value?.stripe_subscription?.plan?.interval) {
        return null;
    }

    return StripeSubscriptionService.recurringLabels[data.value.stripe_subscription.plan.interval];
});

const hasDiscountApplied = computed(() => {
    return data.value?.stripe_subscription?.discount?.coupon;
});

const discountedPrice = computed(() => {
    if (!hasDiscountApplied.value) {
        return data.value?.stripe_subscription?.plan?.formatted?.amount || 0;
    }

    if (data.value?.stripe_subscription?.discount?.coupon?.percent_off) {
        return data.value.stripe_subscription.plan.formatted.amount
            * (1 - (data.value.stripe_subscription.discount.coupon.percent_off / 100));
    }

    if (data.value?.stripe_subscription?.discount?.coupon?.amount_off) {
        return data.value.stripe_subscription.plan.formatted.amount
            - (data.value.stripe_subscription.discount.coupon.formatted.amount_off ?? 0);
    }

    return data.value?.stripe_subscription?.plan?.formatted?.amount || 0;
});
</script>

<template>
  <div v-if="isLoading">
    <Skeleton class="w-36 h-6" />
  </div>
  <div v-else-if="data" class="flex justify-between gap-18">
    <div class="flex gap-2">
      <p class="font-bold">
        Plan actuel
      </p>
      <div>
        <p
          :class="{
            'line-through': hasDiscountApplied,
            'font-bold text-primary': !hasDiscountApplied
          }"
        >
          {{ data.stripe_subscription?.plan.formatted.amount }}€ / {{ recurringLabel }}
        </p>
        <p v-if="hasDiscountApplied" class="font-medium text-primary">
          {{ discountedPrice }}€ / {{ recurringLabel }}
        </p>
      </div>
    </div>
    <div v-if="hasDiscountApplied" class="text-primary flex relative">
      <p>
        Grâce à votre bon de réduction <span class="underline">{{
          data.stripe_subscription?.discount?.coupon?.name
        }}</span>
      </p>
      <DotLottieVue
        src="animations/gift.lottie"
        class="h-14 w-14 relative -top-7"
        autoplay loop
      />
    </div>
  </div>
</template>

<style scoped>

</style>
